import type { FC, ReactNode } from 'react'

import { ArrowLeft, ArrowRight, Maximize2 } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { formatNumber, pad } from '@/lib/format'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'

import {
    CourseMap,
    MAP_HEIGHT,
    MAP_WIDTH
} from '@/components/landing/course-map'

import {
    DESKTOP,
    FULL_MOTION,
    gsap,
    ScrollTrigger,
    useGSAP
} from '@/lib/gsap'

import { courseTotals, holes } from '@/constants/course'

type Mode = 'scroll' | 'interactive'

interface Camera {
    x: number
    y: number
    w: number
    h: number
}

const INTRO = 0.05
const OUTRO = 0.06
const SPAN = 1 - INTRO - OUTRO

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value))
const easeInOut = (t: number): number => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)

const fullView = (): Camera => ({ x: 0, y: 0, w: MAP_WIDTH, h: MAP_HEIGHT })

const framing = (cx: number, cy: number, zoom: number): Camera => {
    const w = MAP_WIDTH / zoom
    const h = MAP_HEIGHT / zoom

    return {
        x: clamp(cx - w / 2, 0, MAP_WIDTH - w),
        y: clamp(cy - h / 2, 0, MAP_HEIGHT - h),
        w,
        h
    }
}

interface CourseMapSectionProps {
    index?: string
}

export const CourseMapSection: FC<CourseMapSectionProps> = ({ index = '02' }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const pinRef = useRef<HTMLDivElement>(null)
    const svgRef = useRef<SVGSVGElement>(null)
    const ballRef = useRef<SVGGElement>(null)
    const routeRefs = useRef<(SVGPathElement | null)[]>([])

    const modeRef = useRef<Mode>('interactive')
    const triggerRef = useRef<ScrollTrigger | null>(null)
    const activeRef = useRef(0)
    const cameraRef = useRef<Camera>(fullView())
    const selectRef = useRef<(index: number) => void>(() => undefined)

    const [active, setActive] = useState(0)
    const [zoomed, setZoomed] = useState(false)

    const { scrollTo } = useSmoothScroll()

    const applyCamera = useCallback((): void => {
        const svg = svgRef.current
        const camera = cameraRef.current
        if (!svg) return

        svg.setAttribute('viewBox', `${camera.x.toFixed(2)} ${camera.y.toFixed(2)} ${camera.w.toFixed(2)} ${camera.h.toFixed(2)}`)
    }, [])

    useGSAP(() => {
        const section = sectionRef.current
        const svg = svgRef.current
        const ball = ballRef.current
        const paths = routeRefs.current.filter((path): path is SVGPathElement => Boolean(path))

        if (!section || !svg || !ball || paths.length !== holes.length) return

        const lengths = paths.map((path) => path.getTotalLength())

        const placeBall = (index: number, fraction: number): DOMPoint => {
            const point = paths[index].getPointAtLength(lengths[index] * clamp(fraction, 0, 1))
            ball.setAttribute('transform', `translate(${point.x.toFixed(2)} ${point.y.toFixed(2)})`)
            return point
        }

        const setActiveHole = (index: number): void => {
            if (activeRef.current === index) return
            activeRef.current = index
            setActive(index)
        }

        const mm = gsap.matchMedia()

        // Desktop: the section pins and the scroll walks the course hole by hole.
        mm.add(`${FULL_MOTION} and ${DESKTOP}`, () => {
            modeRef.current = 'scroll'

            paths.forEach((path, index) => {
                path.style.strokeDasharray = `${lengths[index]} ${lengths[index]}`
                path.style.strokeDashoffset = `${lengths[index]}`
            })

            const target = { progress: 0 }
            const current = { progress: 0 }
            const camera = cameraRef.current
            const ZOOM = 2.1

            const render = (progress: number): void => {
                const walked = clamp((progress - INTRO) / SPAN, 0, 1) * holes.length
                const index = Math.min(holes.length - 1, Math.floor(walked))

                paths.forEach((path, i) => {
                    const drawn = clamp(walked - i, 0, 1)
                    path.style.strokeDashoffset = `${lengths[i] * (1 - drawn)}`
                })

                const point = placeBall(index, walked - index)
                const zoomIn = easeInOut(clamp(progress / INTRO, 0, 1))
                const zoomOut = easeInOut(clamp((progress - (1 - OUTRO)) / OUTRO, 0, 1))
                const zoom = 1 + (ZOOM - 1) * zoomIn * (1 - zoomOut)
                const goal = framing(point.x, point.y, zoom)

                camera.x += (goal.x - camera.x) * 0.14
                camera.y += (goal.y - camera.y) * 0.14
                camera.w += (goal.w - camera.w) * 0.14
                camera.h += (goal.h - camera.h) * 0.14

                applyCamera()
                setActiveHole(index)
            }

            const tick = (): void => {
                current.progress += (target.progress - current.progress) * 0.12
                if (Math.abs(target.progress - current.progress) < 0.00005) current.progress = target.progress
                render(current.progress)
            }

            let ticking = false
            const startTicking = (): void => {
                if (ticking) return
                ticking = true
                gsap.ticker.add(tick)
            }
            const stopTicking = (): void => {
                if (!ticking) return
                ticking = false
                gsap.ticker.remove(tick)
            }

            triggerRef.current = ScrollTrigger.create({
                trigger: section,
                start: 'top top',
                end: () => `+=${Math.round(window.innerHeight * 4.2)}`,
                pin: pinRef.current,
                invalidateOnRefresh: true,
                onUpdate: (self) => {
                    target.progress = self.progress
                },
                onRefresh: (self) => {
                    target.progress = self.progress
                    current.progress = self.progress
                    render(self.progress)
                }
            })

            const visibility = ScrollTrigger.create({
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                onToggle: (self) => {
                    if (self.isActive) startTicking()
                    else stopTicking()
                }
            })

            if (visibility.isActive) startTicking()

            return () => {
                stopTicking()
                triggerRef.current = null
                modeRef.current = 'interactive'
                paths.forEach((path) => {
                    path.style.strokeDasharray = ''
                    path.style.strokeDashoffset = ''
                })
                cameraRef.current = fullView()
                applyCamera()
            }
        })

        // Phones, tablets and reduced motion: tap a hole to fly to it.
        mm.add({ reduce: '(prefers-reduced-motion: reduce)', compact: '(max-width: 1023px)' }, (context) => {
            const { reduce, compact } = context.conditions as { reduce: boolean, compact: boolean }
            if (!reduce && !compact) return

            modeRef.current = 'interactive'

            const camera = cameraRef.current
            const duration = reduce ? 0 : 1.3
            let ballTween: gsap.core.Tween | undefined
            let cameraTween: gsap.core.Tween | undefined

            if (!reduce) {
                paths.forEach((path, index) => {
                    path.style.strokeDasharray = `${lengths[index]} ${lengths[index]}`
                    path.style.strokeDashoffset = `${lengths[index]}`
                })

                gsap.to(paths, {
                    strokeDashoffset: 0,
                    duration: 1.1,
                    ease: 'power2.inOut',
                    stagger: 0.09,
                    scrollTrigger: {
                        trigger: svg,
                        start: 'top 80%',
                        once: true
                    }
                })
            }

            placeBall(activeRef.current, 0)

            selectRef.current = (index: number): void => {
                setActiveHole(index)
                setZoomed(true)

                const hole = holes[index]
                const goal = framing(
                    (hole.tee[0] + hole.green[0]) / 2,
                    (hole.tee[1] + hole.green[1]) / 2,
                    2.2
                )

                cameraTween?.kill()
                cameraTween = gsap.to(camera, {
                    ...goal,
                    duration,
                    ease: 'power3.inOut',
                    overwrite: true,
                    onUpdate: applyCamera,
                    onComplete: applyCamera
                })

                const travel = { fraction: 0 }
                ballTween?.kill()
                ballTween = gsap.to(travel, {
                    fraction: 1,
                    duration: reduce ? 0 : 1.6,
                    delay: reduce ? 0 : 0.35,
                    ease: 'power2.inOut',
                    onStart: () => placeBall(index, 0),
                    onUpdate: () => placeBall(index, travel.fraction),
                    onComplete: () => placeBall(index, 1)
                })
            }

            return () => {
                cameraTween?.kill()
                ballTween?.kill()
                selectRef.current = () => undefined
                paths.forEach((path) => {
                    path.style.strokeDasharray = ''
                    path.style.strokeDashoffset = ''
                })
            }
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    const goTo = useCallback((index: number): void => {
        const next = (index + holes.length) % holes.length
        const trigger = triggerRef.current

        if (modeRef.current === 'scroll' && trigger) {
            const progress = INTRO + ((next + 0.5) / holes.length) * SPAN
            scrollTo(trigger.start + (trigger.end - trigger.start) * progress, { duration: 1.4 })
            return
        }

        selectRef.current(next)
    }, [scrollTo])

    const resetView = useCallback((): void => {
        setZoomed(false)
        gsap.to(cameraRef.current, {
            ...fullView(),
            duration: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1.1,
            ease: 'power3.inOut',
            overwrite: true,
            onUpdate: applyCamera,
            onComplete: applyCamera
        })
    }, [applyCamera])

    const hole = holes[active]

    return (
        <section
            ref={sectionRef}
            id='course-map'
            aria-labelledby='course-map-heading'
            className='relative z-10 bg-sand text-charcoal'
        >
            <div ref={pinRef} className='relative lg:h-[100svh] lg:min-h-[640px] lg:overflow-hidden'>
                <div className='grid gap-10 px-5 py-20 sm:px-8 lg:h-full lg:grid-cols-12 lg:gap-10 lg:px-12 lg:pb-8 lg:pt-20'>
                    <div className='flex flex-col lg:col-span-4 lg:justify-between lg:py-2'>
                        <div>
                            <SectionLabel index={index} className='text-charcoal/70'>
                                The course
                            </SectionLabel>

                            <SplitReveal
                                as='h2'
                                id='course-map-heading'
                                className='mt-6 text-[clamp(2.1rem,3.6vw,3.4rem)] font-light leading-[1.02] tracking-[-0.03em]'
                            >
                                Eighteen holes. <em className='font-serif font-normal'>One unbroken walk.</em>
                            </SplitReveal>

                            <p className='mt-5 max-w-sm text-[15px] leading-relaxed text-charcoal/70 lg:short:hidden'>
                                The routing heads out along the coast, climbs through the pines and turns for home across the heath.
                                <span className='hidden lg:inline'> Keep scrolling to walk it hole by hole.</span>
                                <span className='lg:hidden'> Choose a hole to fly to it.</span>
                            </p>
                        </div>

                        <div className='mt-10 lg:mt-6' aria-live='polite'>
                            <div key={hole.number} className='animate-in fade-in slide-in-from-bottom-3 duration-700 ease-out'>
                                <div className='flex items-end gap-5'>
                                    <span className='font-serif text-[clamp(4rem,6vw,6.25rem)] leading-[0.78] tracking-[-0.02em] text-forest'>
                                        {pad(hole.number)}
                                    </span>

                                    <div className='pb-1'>
                                        <p className='font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55'>
                                            Hole {hole.number} of 18
                                        </p>

                                        <h3 className='type-wide mt-1 text-[clamp(1.35rem,2vw,1.8rem)] font-medium uppercase leading-tight'>
                                            {hole.name}
                                        </h3>
                                    </div>
                                </div>

                                <dl className='mt-6 grid grid-cols-3 border-y border-charcoal/15'>
                                    {[
                                        { label: 'Par', value: String(hole.par) },
                                        { label: 'Yards', value: formatNumber(hole.yards) },
                                        { label: 'Stroke index', value: String(hole.strokeIndex) }
                                    ].map((item, index) => (
                                        <div key={item.label} className={cn('py-3', index > 0 && 'border-l border-charcoal/15 pl-4')}>
                                            <dt className='font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/55'>
                                                {item.label}
                                            </dt>

                                            <dd className='type-wide mt-1 text-xl font-medium tabular-nums'>
                                                {item.value}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>

                                <p className='mt-5 min-h-[4.5rem] max-w-md text-[15px] leading-relaxed text-charcoal/75'>
                                    {hole.description}
                                </p>
                            </div>
                        </div>

                        <div className='mt-8 lg:mt-6'>
                            <div className='flex items-center justify-between gap-4'>
                                <p className='font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal/55'>
                                    Choose a hole
                                </p>

                                <div className='flex gap-2'>
                                    <button
                                        type='button'
                                        onClick={() => goTo(active - 1)}
                                        className='flex size-9 items-center justify-center rounded-full border border-charcoal/20 transition-colors hover:border-charcoal hover:bg-charcoal hover:text-bone'
                                    >
                                        <ArrowLeft className='size-4' aria-hidden='true' />
                                        <span className='sr-only'>
                                            Previous hole
                                        </span>
                                    </button>

                                    <button
                                        type='button'
                                        onClick={() => goTo(active + 1)}
                                        className='flex size-9 items-center justify-center rounded-full border border-charcoal/20 transition-colors hover:border-charcoal hover:bg-charcoal hover:text-bone'
                                    >
                                        <ArrowRight className='size-4' aria-hidden='true' />
                                        <span className='sr-only'>
                                            Next hole
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div role='group' aria-label='Holes one to eighteen' className='mt-3 grid grid-cols-9 gap-1.5'>
                                {holes.map((item, index) => (
                                    <button
                                        key={item.number}
                                        type='button'
                                        onClick={() => goTo(index)}
                                        aria-pressed={index === active}
                                        aria-label={`Hole ${item.number}, ${item.name}, par ${item.par}, ${item.yards} yards`}
                                        className={cn(
                                            'flex h-9 items-center justify-center rounded-[3px] border font-mono text-[11px] tabular-nums transition-colors duration-300 lg:h-8',
                                            index === active
                                                ? 'border-forest bg-forest text-bone'
                                                : index < active
                                                    ? 'border-charcoal/25 bg-charcoal/[0.06] text-charcoal'
                                                    : 'border-charcoal/15 text-charcoal/60 hover:border-charcoal/50 hover:text-charcoal'
                                        )}
                                    >
                                        {pad(item.number)}
                                    </button>
                                ))}
                            </div>

                            <dl className='mt-5 grid grid-cols-3 gap-4 text-sm lg:short:hidden'>
                                <div>
                                    <dt className='font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/55'>
                                        Out
                                    </dt>

                                    <dd className='mt-1 tabular-nums'>
                                        Par {courseTotals.outPar} · {formatNumber(courseTotals.outYards)}
                                    </dd>
                                </div>

                                <div>
                                    <dt className='font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/55'>
                                        In
                                    </dt>

                                    <dd className='mt-1 tabular-nums'>
                                        Par {courseTotals.inPar} · {formatNumber(courseTotals.inYards)}
                                    </dd>
                                </div>

                                <div>
                                    <dt className='font-mono text-[10px] uppercase tracking-[0.2em] text-charcoal/55'>
                                        Total
                                    </dt>

                                    <dd className='mt-1 tabular-nums'>
                                        Par {courseTotals.par} · {formatNumber(courseTotals.yards)}
                                    </dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <div className='lg:col-span-8'>
                        <div className='relative aspect-[25/16] w-full overflow-hidden rounded-[6px] border border-charcoal/10 bg-[#f5f2eb] shadow-[0_40px_80px_-40px_rgba(27,27,25,0.35)] lg:aspect-auto lg:h-full'>
                            <CourseMap
                                activeIndex={active}
                                svgRef={svgRef}
                                routeRefs={routeRefs}
                                ballRef={ballRef}
                                className='absolute inset-0 h-full w-full'
                            />

                            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-3 sm:p-5'>
                                <p className='rounded-full bg-paper/85 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] text-charcoal/70 backdrop-blur sm:text-[10px]'>
                                    Championship 18 · Routing
                                </p>

                                <svg viewBox='0 0 40 40' className='size-9 text-charcoal sm:size-11'>
                                    <circle cx='20' cy='20' r='18' fill='#f8f6f1' fillOpacity='0.85' stroke='currentColor' strokeOpacity='0.2' />
                                    <path d='M20 7l4.5 13H15.5z' fill='#a5683f' />
                                    <path d='M20 33l-4.5-13h9z' fill='currentColor' fillOpacity='0.35' />
                                    <text x='20' y='6' textAnchor='middle' fontSize='5' fill='currentColor' className='font-mono'>
                                        N
                                    </text>
                                </svg>
                            </div>

                            <div className='pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-3 sm:p-5'>
                                <div aria-hidden='true' className='hidden rounded-full bg-paper/85 px-3 py-2 backdrop-blur sm:block'>
                                    <div className='flex h-1.5 w-28 overflow-hidden rounded-full border border-charcoal/30'>
                                        <span className='h-full w-1/2 bg-charcoal/70' />
                                    </div>

                                    <div className='mt-1 flex justify-between font-mono text-[9px] text-charcoal/60'>
                                        <span>0</span>
                                        <span>200 m</span>
                                    </div>
                                </div>

                                <div className='ml-auto flex items-center gap-2'>
                                    {zoomed && (
                                        <button
                                            type='button'
                                            onClick={resetView}
                                            className='pointer-events-auto flex h-8 items-center gap-2 rounded-full bg-charcoal px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-bone'
                                        >
                                            <Maximize2 className='size-3.5' aria-hidden='true' />
                                            Whole course
                                        </button>
                                    )}

                                    <p className='rounded-full bg-charcoal/90 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-bone sm:text-[10px]'>
                                        Hole {pad(hole.number)} · Par {hole.par} · {hole.yards} yds
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
